import { type PayloadAction, createSlice, isAnyOf } from "@reduxjs/toolkit"
import { REHYDRATE } from "redux-persist/es/constants"

import { addPrimitiveCases } from "../../../redux/shared/add-primitive-cases.ts"
import {
  afterFailedFetch,
  afterFetch,
  SCROLL_PAGE_SIZE
} from "../../../redux/shared/paged-list.ts"
import { generateSetPropReducer } from "../../../redux/shared/set-setate-prop.reducer.ts"
import type { RehydrateAction } from "../../../store.ts"
import {
  PROJECT_STATUSES_OBJ,
  isArchivingStatus
} from "../../Projects/constants/project-statuses.ts"
import { closesSuggestion } from "../../StatusAutomation/helpers/status-automation.ts"
import type { StatusRefusal } from "../../StatusAutomation/types/status-automation.types.ts"
import {
  acceptProjectStatusSuggestion,
  dismissProjectStatusSuggestion,
  refreshProjectStatus,
  undoProjectStatusTransition
} from "./project-status-automation.thunks.ts"
import { getEntityName } from "../../../utils/user/get-entity-name"
import { logout } from "../../LoginPage/redux/auth.thunks.ts"
import {
  FINANCE_ITEM_TYPES,
  type FinanceItemTypes
} from "../ProjectViewPage/constants/finance-item-constants.ts"
import type {
  FinancialTransactionItem,
  FinancialTransactionSummary
} from "../types/project-finance.types.tsx"
import type { ProjectChecklistItem } from "../types/project-checklists.types.tsx"
import type { ProjectInvoice } from "../types/project-invoices.types.tsx"
import type { ProjectMilestone } from "../types/project-phases.types.tsx"
import { type Project } from "../types/project.types.ts"
import {
  deleteChecklistItem,
  fetchChecklistItems,
  fetchChecklistItemsFilterOptions
} from "./project-checklist/project-checklist.thunks.ts"
import {
  fetchProjectCommissions,
  patchProjectCommission
} from "./project-commissions/project-commissions.thunks.ts"
import {
  deleteFinancialTransactionItem,
  fetchFinancialAlerts,
  fetchFinancialSnapshot,
  setFinancialAlertsMuted,
  fetchFinancialTransactionItems,
  fetchFinancialTransactionItemsFilterOptions,
  patchFinancialTransactionItem
} from "./project-finance/project-finance.thunks.ts"
import {
  createInvoice,
  deleteInvoice,
  fetchInvoiceSnapshot,
  fetchInvoices,
  patchInvoice
} from "./project-invoices/project-invoices.thunks.ts"
import {
  deleteInvoice as deleteGeneratedInvoice,
  generateInvoice,
  issueInvoice,
  patchGeneratedInvoice,
  sendInvoice,
  voidInvoice
} from "../../Invoices/redux/invoices.thunks.ts"
import {
  createMilestoneDependency,
  deleteMilestoneDependency,
  fetchProjectMilestoneDependencies,
  patchMilestoneDependency
} from "./project-milestones/project-milestone-dependencies.thunks.ts"
import {
  createProjectMilestone,
  deleteProjectMilestone,
  fetchAlertContributingMilestones,
  fetchProjectMilestoneById,
  fetchProjectMilestoneSnapshot,
  fetchProjectMilestones,
  fetchProjectMilestonesFilterOptions,
  patchProjectMilestone
} from "./project-milestones/project-milestones.thunks.ts"
import { refreshInFlight } from "./list-refresh.ts"
import { fetchProjectSubcontractors } from "./project-subcontractors/project-subcontractors.thunks.ts"
import {
  initialFinancialTransactionFilters,
  projectSliceName
} from "./project.constants.ts"
import {
  addProjectTypeToProject,
  createAndAssignSubcontractor,
  createProject,
  fetchProject,
  fetchProjectFiles,
  fetchProjectLogHistory,
  refreshProjectLogHistory,
  removeProjectTypeToProject,
  reorderProjectTypes,
  updateProject,
  updateProjectTypeToProject
} from "./project.thunks.ts"
import {
  FETCHING_SNAPSHOT_ENUM,
  type ProjectPageRead,
  type ProjectState,
  type RefreshableProjectList
} from "./project.types.ts"

type PageReadMeta = { requestId: string }

const startPageRead = (
  state: ProjectState,
  read: ProjectPageRead,
  projectId: string,
  { requestId }: PageReadMeta
) => {
  state.pageReads[read] = {
    projectId,
    requestId,
    status: "pending",
    offered: false
  }
}

// Only the newest read of each kind may land: one still out for the project
// opened before this one must not fill this one's page.
const settlePageRead = (
  state: ProjectState,
  read: ProjectPageRead,
  { requestId }: PageReadMeta,
  status: "done" | "failed"
) => {
  const entry = state.pageReads[read]
  if (entry?.requestId !== requestId) return false
  entry.status = status
  return true
}

function parseValue(value: FinancialTransactionItem["value"]) {
  return parseFloat(value ?? 0)
}

function updateSummary(
  summary: FinancialTransactionSummary,
  itemType: FinanceItemTypes,
  delta: number
) {
  if (itemType === FINANCE_ITEM_TYPES.RECEIVABLE) {
    summary.receivable += delta
  } else if (itemType === FINANCE_ITEM_TYPES.PAYABLE) {
    summary.payable += delta
  }
}

const initialState: ProjectState = {
  project: null,
  projectFiles: { projectId: null, requestId: null, status: "idle", items: [] },
  projectRequestId: null,
  pageReads: {},
  milestoneSnapshot: null,
  financeFilterOptions: {},

  milestones: {
    count: 0,
    results: [],
    next: null,
    previous: null
  },
  milestonesRequestId: null,
  milestoneWriteSeq: 0,
  milestoneRowWrittenAt: {},
  milestoneReadStartedAt: {},
  milestonesFilters: {
    page: 1,
    page_size: SCROLL_PAGE_SIZE,
    project_type: null,
    project_type_assignment: null
  },
  milestonesFilterOptions: {},
  milestoneDependencies: [],

  financialTransactionItems: {
    count: 0,
    results: [],
    next: null,
    previous: null,
    summary: {
      payable: 0,
      receivable: 0,
      receivable_collected: 0,
      payable_paid: 0
    }
  },
  financialTransactionItemsRequestId: null,
  financialTransactionFilters: initialFinancialTransactionFilters,
  financialTransactionSnapshot: null,
  financialAlerts: null,
  financialAlertsMuteRequestId: null,
  financialSnapshotRequestedFor: null,
  financialSnapshotRequestId: null,
  financialAlertsRequestId: null,
  financialAlertsRequestedFor: null,
  alertContributingMilestones: [],

  invoices: {
    count: 0,
    results: [],
    next: null,
    previous: null
  },
  invoicesRequestId: null,
  invoicesFilters: {
    page: 1,
    page_size: 15,
    q: null,
    type: null,
    status: null
  },
  invoiceSnapshot: null,

  subcontractors: {
    count: 0,
    results: [],
    next: null,
    previous: null
  },
  subcontractorsRequestId: null,

  subcontractorsFilters: { page: 1, page_size: 15, q: null },
  selectedSubcontractor: null,

  checklistItems: {
    count: 0,
    results: [],
    next: null,
    previous: null
  },
  checklistItemsRequestId: null,
  checklistItemsFilters: {
    page: 1,
    page_size: 15,
    q: null
  },
  checklistItemsFilterOptions: {
    project_phases: [],
    status: [],
    subcontractors: [],
    team_members: [],
    admins: [],
    project_managers: [],
    priority: [],
    project_type: [],
    milestones: []
  },
  listRefreshRequestIds: {},

  logs: [],
  logsRequestId: null,

  commissions: {
    rows: [],
    summary: {
      office_lead_cost_plan: 0,
      total_lead_cost_plan: 0,
      office_lead_cost_actual: 0,
      others_lead_cost_actual: 0,
      others_lead_cost_plan: 0,
      referral_lead_cost_actual: 0,
      referral_lead_cost_plan: 0,
      total_lead_cost_actual: 0,
      sales_commission_actual: 0,
      sales_commission_plan: 0,
      sales_commission_committed: 0,
      sales_commission_settled: 0
    }
  },
  commissionsItemsFilters: {
    page: 1,
    page_size: 10,
    q: null
  },

  showSnapshot: false,
  financialAlertFocusRule: null,
  fetching: false,
  loadError: null,
  fetchingSection: false,
  fetchingSnapshotMap: {
    [FETCHING_SNAPSHOT_ENUM.FINANCIAL]: false,
    [FETCHING_SNAPSHOT_ENUM.INVOICE]: false,
    [FETCHING_SNAPSHOT_ENUM.MILESTONES]: false
  },
  fetchingSubcontractors: false,
  saveResult: null,
  saveFetching: false
}

const applyProjectStatus = (project: Project, status: number | undefined) => {
  if (status == null) return
  project.project_status = status
  project.project_status_name =
    PROJECT_STATUSES_OBJ[status]?.label ?? project.project_status_name
}

// A suggestion the server says is answered or overtaken can never be acted on.
const dropClosedProjectSuggestion = (
  state: ProjectState,
  arg: { projectId: string; suggestionId: string },
  payload: unknown
) => {
  if (state.project?.id !== arg.projectId) return
  if (!closesSuggestion(payload as StatusRefusal | null)) return
  state.project.status_suggestions = (
    state.project.status_suggestions ?? []
  ).filter((suggestion) => suggestion.id !== arg.suggestionId)
}

const loadMoreWaitsForRefresh = (
  state: ProjectState,
  list: RefreshableProjectList,
  meta: { arg?: { isMore?: boolean } }
) => !!meta.arg?.isMore && refreshInFlight(state, list)

const trackRefresh = (
  state: ProjectState,
  list: RefreshableProjectList,
  meta: { requestId: string; arg?: { refresh?: boolean } }
) => {
  if (meta.arg?.refresh) state.listRefreshRequestIds[list] = meta.requestId
  else delete state.listRefreshRequestIds[list]
}

// The id of the newest history read is kept after it lands, so an older one
// answering later is still recognised and dropped. An older answer for the
// open project still fills an empty history, since the newer read may fail.
const isStaleLogsRead = (
  state: ProjectState,
  meta: { requestId: string; arg: string }
) =>
  !!state.logsRequestId &&
  state.logsRequestId !== meta.requestId &&
  (state.logs.length > 0 || state.project?.id !== meta.arg)

// What only the list read returns; a write's response leaves them out.
const MILESTONE_LIST_ONLY_FIELDS = [
  "dependency_count",
  "schedule_risk"
] as const

// Ends a milestone read's bookkeeping and answers, per row, whether that row
// was written after the read started: the read then carries its old values.
const settleMilestoneRead = (state: ProjectState, requestId: string) => {
  const startedAt = state.milestoneReadStartedAt[requestId]
  delete state.milestoneReadStartedAt[requestId]
  return (id: string | undefined) =>
    startedAt !== undefined &&
    !!id &&
    (state.milestoneRowWrittenAt[id] ?? 0) > startedAt
}

// Stamped when a write starts and again when it settles: a read that starts
// while the write is in flight may carry the row's pre-write values.
const stampMilestoneWrite = (state: ProjectState, id: string) => {
  state.milestoneWriteSeq += 1
  state.milestoneRowWrittenAt[id] = state.milestoneWriteSeq
}

const keepWrittenRow = (kept: ProjectMilestone, read: ProjectMilestone) => {
  const listOnly: Partial<ProjectMilestone> = {}
  for (const field of MILESTONE_LIST_ONLY_FIELDS) {
    if (field in read) Object.assign(listOnly, { [field]: read[field] })
  }
  return { ...kept, ...listOnly }
}

const projectSlice = createSlice({
  name: projectSliceName,
  initialState,
  reducers: {
    projectSetProp: generateSetPropReducer<ProjectState>(),
    projectPageReadsOffered: (
      state,
      action: PayloadAction<{
        projectId: string
        reads: ProjectPageRead[]
        at: number
      }>
    ) => {
      for (const read of action.payload.reads) {
        const entry = state.pageReads[read]
        if (entry?.projectId !== action.payload.projectId) continue
        entry.offered = true
        entry.offeredAt = action.payload.at
      }
    },
    projectPageReadTaken: (state, action: PayloadAction<ProjectPageRead>) => {
      const entry = state.pageReads[action.payload]
      if (entry) entry.offered = false
    },
    projectInvoiceUpdated: (state, action: PayloadAction<ProjectInvoice>) => {
      const invoiceIndex = state.invoices.results.findIndex(
        (invoice) => invoice.id === action.payload.id
      )
      if (invoiceIndex === -1) return

      const statusFilter = state.invoicesFilters.status
      if (statusFilter && action.payload.status !== statusFilter) {
        state.invoices.results.splice(invoiceIndex, 1)
        state.invoices.count = Math.max(0, state.invoices.count - 1)
        return
      }

      state.invoices.results[invoiceIndex] = {
        ...state.invoices.results[invoiceIndex],
        ...action.payload
      }
    },
    // Applied before the reorder request so the tabs settle where they were
    // dropped instead of snapping back until the response lands.
    projectSetProjectTypes: (
      state,
      action: PayloadAction<Project["project_type_details"]>
    ) => {
      if (state?.project) {
        state.project.project_type_details = action.payload || []
      }
    },
    // Same optimistic treatment for milestone rows dragged in the list.
    projectSetMilestoneOrder: (
      state,
      action: PayloadAction<ProjectState["milestones"]["results"]>
    ) => {
      state.milestones.results = action.payload || []
    },
    projectSetChecklistOrder: (
      state,
      action: PayloadAction<ProjectState["checklistItems"]["results"]>
    ) => {
      state.checklistItems.results = action.payload || []
    },
    // Merged, not replaced: a write answer leaves out the list-only fields
    // (project_id, project_name, financial_items).
    projectMergeChecklistItem: (
      state,
      action: PayloadAction<Partial<ProjectChecklistItem> & { id: string }>
    ) => {
      const row = state.checklistItems.results?.find(
        (item) => item.id === action.payload.id
      )
      if (row) Object.assign(row, action.payload)
    }
  },
  extraReducers: (builder) => {
    addPrimitiveCases(builder, createProject, "saveFetching")
    builder.addCase(REHYDRATE, (state, action: RehydrateAction) => {
      const status = action?.payload?.project
      if (!status) return state

      return { ...status, selectedSubcontractor: null }
    })
    builder
      // fetchProjectMilestones
      .addCase(fetchProjectMilestones.pending, (state, action) => {
        state.fetchingSection = true
        state.milestonesRequestId = action.meta.requestId
        state.milestoneReadStartedAt[action.meta.requestId] =
          state.milestoneWriteSeq

        // A refresh re-reads the rows already on screen: emptying the list
        // first would unmount them and take the scroll position with them.
        if (!action?.meta?.arg?.isMore && !action?.meta?.arg?.refresh) {
          state.milestones = initialState.milestones
        }
      })
      .addCase(fetchProjectMilestones.fulfilled, (state, action) => {
        const writtenSince = settleMilestoneRead(state, action.meta.requestId)
        if (state.milestonesRequestId !== action.meta.requestId) return
        state.fetchingSection = false
        state.milestonesRequestId = null

        const stored = new Map(
          (state.milestones?.results ?? []).map((m) => [m?.id, m])
        )
        const fetched = afterFetch(
          state.milestones,
          action?.payload.data,
          action?.payload?.isMore
        )
        // A row saved after this read started keeps its saved values; the
        // read would put its pre-save copy back. A list read without lines
        // keeps the lines an expanded row already loaded; they are re-read
        // on their own after a write to that row.
        // A new object: what afterFetch returns may be the (frozen) payload.
        state.milestones = {
          ...fetched,
          results: fetched.results?.map((m) => {
            const kept = m ? stored.get(m.id) : undefined
            if (kept && writtenSince(m.id)) return keepWrittenRow(kept, m)
            return m &&
              !("financial_items" in m) &&
              kept?.financial_items !== undefined
              ? { ...m, financial_items: kept.financial_items }
              : m
          })
        }
      })
      .addCase(fetchProjectMilestoneById.pending, (state, action) => {
        state.milestoneReadStartedAt[action.meta.requestId] =
          state.milestoneWriteSeq
      })
      .addCase(fetchProjectMilestoneById.fulfilled, (state, action) => {
        const writtenSince = settleMilestoneRead(state, action.meta.requestId)
        if (writtenSince(action.payload?.id)) return
        state.milestones = {
          ...state.milestones,
          results: state.milestones?.results?.map((m) =>
            m?.id === action.payload?.id ? { ...m, ...action.payload } : m
          )
        }
      })
      .addCase(fetchProjectMilestoneById.rejected, (state, action) => {
        settleMilestoneRead(state, action.meta.requestId)
      })
      .addCase(fetchProjectMilestones.rejected, (state, action) => {
        settleMilestoneRead(state, action.meta.requestId)
        if (state.milestonesRequestId !== action.meta.requestId) return
        state.fetchingSection = false
        state.milestonesRequestId = null
        if (action?.meta?.arg?.refresh) return
        state.milestones = afterFailedFetch(
          state.milestones,
          initialState.milestones,
          action?.meta?.arg?.isMore
        )
      })

      // status automation
      .addCase(acceptProjectStatusSuggestion.fulfilled, (state, action) => {
        if (state.project?.id !== action.meta.arg.projectId) return
        const { project, project_status, status_suggestions } = action.payload
        if (project) {
          state.project = project
          return
        }
        applyProjectStatus(state.project, project_status)
        state.project.status_suggestions = status_suggestions ?? []
      })
      .addCase(acceptProjectStatusSuggestion.rejected, (state, action) =>
        dropClosedProjectSuggestion(state, action.meta.arg, action.payload)
      )
      .addCase(dismissProjectStatusSuggestion.fulfilled, (state, action) => {
        if (state.project?.id !== action.meta.arg.projectId) return
        state.project.status_suggestions =
          action.payload.status_suggestions ?? []
      })
      .addCase(dismissProjectStatusSuggestion.rejected, (state, action) =>
        dropClosedProjectSuggestion(state, action.meta.arg, action.payload)
      )
      .addCase(refreshProjectStatus.fulfilled, (state, action) => {
        const fresh = action.payload
        if (!state.project || state.project.id !== fresh?.id) return
        // A close archives the project as well as moving it, which every
        // section reads; any other move touches the status and its offers.
        if (
          isArchivingStatus(fresh.project_status) &&
          fresh.project_status !== state.project.project_status
        ) {
          state.project = fresh
          return
        }
        applyProjectStatus(state.project, fresh.project_status)
        state.project.status_suggestions = fresh.status_suggestions ?? []
      })
      .addCase(undoProjectStatusTransition.fulfilled, (state, action) => {
        if (state.project?.id !== action.meta.arg.projectId) return
        applyProjectStatus(state.project, action.payload.project_status)
      })

      // updateProject
      .addCase(updateProject.pending, (state) => {
        state.saveFetching = true
      })
      .addCase(updateProject.fulfilled, (state, action) => {
        state.saveFetching = false
        state.project = action?.payload
        // A save answers with the project's files: an upload or delete from
        // the Overview cards shows without reading them again, and replaces
        // any read still out from before it.
        const files = action?.payload?.attachments
        if (
          Array.isArray(files) &&
          state.projectFiles.projectId === action.payload.id
        ) {
          state.projectFiles = {
            projectId: action.payload.id,
            requestId: null,
            status: "done",
            items: files
          }
        }
      })

      // fetchProjectFiles
      .addCase(fetchProjectFiles.pending, (state, action) => {
        state.projectFiles = {
          projectId: action.meta.arg,
          requestId: action.meta.requestId,
          status: "loading",
          items:
            state.projectFiles.projectId === action.meta.arg
              ? state.projectFiles.items
              : []
        }
      })
      .addCase(fetchProjectFiles.fulfilled, (state, action) => {
        if (state.projectFiles.requestId !== action.meta.requestId) return
        state.projectFiles.status = "done"
        state.projectFiles.items = action.payload
      })
      .addCase(fetchProjectFiles.rejected, (state, action) => {
        if (state.projectFiles.requestId !== action.meta.requestId) return
        state.projectFiles.status = "failed"
      })
      .addCase(updateProject.rejected, (state) => {
        state.saveFetching = false
      })

      // fetchProjectMilestonesFilterOptions
      .addCase(fetchProjectMilestonesFilterOptions.pending, (state, action) => {
        startPageRead(
          state,
          "milestonesFilterOptions",
          action.meta.arg.project_id,
          action.meta
        )
      })
      .addCase(
        fetchProjectMilestonesFilterOptions.fulfilled,
        (state, action) => {
          if (
            !settlePageRead(
              state,
              "milestonesFilterOptions",
              action.meta,
              "done"
            )
          )
            return
          state.milestonesFilterOptions = action?.payload
        }
      )
      .addCase(
        fetchProjectMilestonesFilterOptions.rejected,
        (state, action) => {
          if (
            !settlePageRead(
              state,
              "milestonesFilterOptions",
              action.meta,
              "failed"
            )
          )
            return
          state.milestonesFilterOptions = initialState.milestonesFilterOptions
        }
      )

      .addCase(
        fetchFinancialTransactionItemsFilterOptions.pending,
        (state, action) => {
          startPageRead(
            state,
            "financeFilterOptions",
            action.meta.arg.project_id,
            action.meta
          )
        }
      )
      .addCase(
        fetchFinancialTransactionItemsFilterOptions.fulfilled,
        (state, action) => {
          if (
            !settlePageRead(state, "financeFilterOptions", action.meta, "done")
          )
            return
          state.financeFilterOptions = action.payload
        }
      )
      .addCase(
        fetchFinancialTransactionItemsFilterOptions.rejected,
        (state, action) => {
          if (
            !settlePageRead(
              state,
              "financeFilterOptions",
              action.meta,
              "failed"
            )
          )
            return
          state.financeFilterOptions = initialState.financeFilterOptions
        }
      )

      .addCase(fetchProjectMilestoneSnapshot.pending, (state, action) => {
        startPageRead(state, "milestoneSnapshot", action.meta.arg, action.meta)
      })
      .addCase(fetchProjectMilestoneSnapshot.fulfilled, (state, action) => {
        if (!settlePageRead(state, "milestoneSnapshot", action.meta, "done"))
          return
        state.milestoneSnapshot = action.payload
      })
      .addCase(fetchProjectMilestoneSnapshot.rejected, (state, action) => {
        settlePageRead(state, "milestoneSnapshot", action.meta, "failed")
      })

      // fetchProject
      .addCase(fetchProject.pending, (state, action) => {
        state.project = null
        // Every open reads the files again: they may have changed elsewhere.
        state.projectFiles = initialState.projectFiles
        state.projectRequestId = action.meta.requestId
        state.fetching = true
        state.loadError = null
        // Nothing read for the project open before this one may show here.
        state.pageReads = {}
        state.milestoneSnapshot = null
        state.invoiceSnapshot = null
        state.milestoneDependencies = initialState.milestoneDependencies
        state.milestonesFilterOptions = initialState.milestonesFilterOptions
        state.financeFilterOptions = initialState.financeFilterOptions
        state.milestones = initialState.milestones
        state.checklistItems = initialState.checklistItems
        state.fetchingSnapshotMap[FETCHING_SNAPSHOT_ENUM.INVOICE] = false
        state.financialTransactionSnapshot = null
        state.financialAlerts = null
        state.financialSnapshotRequestedFor = null
        state.financialAlertsRequestedFor = null
        // A read still out for the previous project must not land in this one.
        state.financialSnapshotRequestId = null
        state.financialAlertsRequestId = null
        state.fetchingSnapshotMap[FETCHING_SNAPSHOT_ENUM.FINANCIAL] = false
      })
      .addCase(fetchProject.fulfilled, (state, action) => {
        if (
          state.projectRequestId &&
          state.projectRequestId !== action.meta.requestId
        )
          return
        const project = action?.payload

        state.fetching = false
        state.project = project
        // An older backend ignores `omit` and still answers with the files
        // (and 404s the files read): take them from here instead.
        if (Array.isArray(project?.attachments))
          state.projectFiles = {
            projectId: project.id,
            requestId: null,
            status: "done",
            items: project.attachments
          }
        state.milestonesFilters = {
          ...initialState.milestonesFilters,
          project_type: project?.project_type_details?.[0]?.id,
          project_type_assignment:
            project?.project_type_details?.[0]?.assignment_id
        }

        state.financialTransactionFilters =
          initialState.financialTransactionFilters
        state.invoicesFilters = initialState.invoicesFilters
        state.checklistItemsFilters = initialState.checklistItemsFilters
      })
      .addCase(fetchProject.rejected, (state, action) => {
        if (
          state.projectRequestId &&
          state.projectRequestId !== action.meta.requestId
        )
          return
        state.fetching = false
        state.project = null
        state.loadError = {
          id: action.meta.arg,
          notFound:
            (action.payload as { status?: number } | undefined)?.status === 404
        }
      })

      // createProjectMilestone
      .addCase(createProjectMilestone.fulfilled, (state, action) => {
        // const isFull =
        //   state.milestones.results.length >= state.milestonesFilters.page_size

        state.milestones.count = state.milestones.count + 1

        if (!state?.milestones?.next) {
          state.milestones.results.push(action?.payload)
        }

        // state.milestones = {
        //   ...state.milestones,
        //   count: (state.milestones.count || 0) + 1,
        //   results:
        // isFull
        // ? state.milestones.results
        // : [...state.milestones.results, action.payload]
        // }
      })

      .addCase(patchProjectMilestone.pending, (state, action) => {
        stampMilestoneWrite(state, action.meta.arg.id)
      })
      .addCase(patchProjectMilestone.rejected, (state, action) => {
        stampMilestoneWrite(state, action.meta.arg.id)
      })
      .addCase(patchProjectMilestone.fulfilled, (state, action) => {
        stampMilestoneWrite(state, action.meta.arg.id)
        // The write's response omits list-only fields (dependency count,
        // schedule risk); keep them until the refresh that follows lands.
        state.milestones = {
          ...state?.milestones,
          results: state?.milestones?.results?.map((m) =>
            m?.id === action?.payload?.id ? { ...m, ...action.payload } : m
          )
        }
      })

      //deleteProjectMilestone
      .addCase(deleteProjectMilestone.fulfilled, (state, action) => {
        state.milestones.count = state.milestones.count - 1
        state.milestones = {
          ...state?.milestones,
          results: state?.milestones?.results?.filter(
            (m) => m?.id !== action?.payload
          )
        }
      })

      .addCase(fetchProjectMilestoneDependencies.pending, (state, action) => {
        startPageRead(
          state,
          "milestoneDependencies",
          action.meta.arg,
          action.meta
        )
      })
      .addCase(fetchProjectMilestoneDependencies.fulfilled, (state, action) => {
        if (
          !settlePageRead(state, "milestoneDependencies", action.meta, "done")
        )
          return
        state.milestoneDependencies = action.payload
      })
      .addCase(fetchProjectMilestoneDependencies.rejected, (state, action) => {
        settlePageRead(state, "milestoneDependencies", action.meta, "failed")
      })

      .addCase(createMilestoneDependency.fulfilled, (state, action) => {
        state.milestoneDependencies.push(action.payload)
      })

      .addCase(patchMilestoneDependency.fulfilled, (state, action) => {
        state.milestoneDependencies = state.milestoneDependencies.map((d) =>
          d.id === action.payload.id ? action.payload : d
        )
      })

      .addCase(deleteMilestoneDependency.fulfilled, (state, action) => {
        state.milestoneDependencies = state.milestoneDependencies.filter(
          (d) => d.id !== action.payload
        )
      })

      .addCase(patchFinancialTransactionItem.fulfilled, (state, action) => {
        const updatedItem = action.payload
        const items = state.financialTransactionItems
        if (!items) return

        const oldItem = items.results?.find((m) => m.id === updatedItem.id)

        if (oldItem && items.summary) {
          updateSummary(
            items.summary,
            oldItem.item_type,
            -parseValue(oldItem.value)
          )
          updateSummary(
            items.summary,
            updatedItem.item_type,
            parseValue(updatedItem.value)
          )
        }

        items.results = items.results?.map((m) =>
          m.id === updatedItem.id ? updatedItem : m
        )
      })

      // fetchFinancialTransactionItems
      .addCase(fetchFinancialTransactionItems.pending, (state, action) => {
        if (
          loadMoreWaitsForRefresh(
            state,
            "financialTransactionItems",
            action.meta
          )
        )
          return
        state.fetchingSection = true
        state.financialTransactionItemsRequestId = action.meta.requestId
        trackRefresh(state, "financialTransactionItems", action.meta)

        if (!action?.meta?.arg?.isMore && !action?.meta?.arg?.refresh) {
          state.financialTransactionItems =
            initialState.financialTransactionItems
        }
      })
      .addCase(fetchFinancialTransactionItems.fulfilled, (state, action) => {
        if (state.financialTransactionItemsRequestId !== action.meta.requestId)
          return
        state.fetchingSection = false
        state.financialTransactionItemsRequestId = null
        delete state.listRefreshRequestIds.financialTransactionItems

        state.financialTransactionItems = afterFetch(
          state.financialTransactionItems,
          action?.payload.data,
          action?.payload?.isMore
        )
      })
      .addCase(fetchFinancialTransactionItems.rejected, (state, action) => {
        if (state.financialTransactionItemsRequestId !== action.meta.requestId)
          return
        state.fetchingSection = false
        state.financialTransactionItemsRequestId = null
        delete state.listRefreshRequestIds.financialTransactionItems
        if (action?.meta?.arg?.refresh) return
        state.financialTransactionItems = afterFailedFetch(
          state.financialTransactionItems,
          initialState.financialTransactionItems,
          action?.meta?.arg?.isMore
        )
      })

      .addCase(deleteFinancialTransactionItem.fulfilled, (state, action) => {
        const id = action.payload
        const items = state.financialTransactionItems
        if (!items) return

        const index = items.results?.findIndex((m) => m.id === id)
        if (index === -1 || index === undefined) return

        const item = items.results[index]

        if (items.summary) {
          updateSummary(items.summary, item.item_type, -parseValue(item.value))
        }

        items.results.splice(index, 1)
      })

      // fetchInvoices
      .addCase(fetchInvoices.pending, (state, action) => {
        if (loadMoreWaitsForRefresh(state, "invoices", action.meta)) return
        state.fetchingSection = true
        state.invoicesRequestId = action.meta.requestId
        trackRefresh(state, "invoices", action.meta)

        if (!action?.meta?.arg?.isMore && !action?.meta?.arg?.refresh) {
          state.invoices = initialState.invoices
        }
      })
      .addCase(fetchInvoices.fulfilled, (state, action) => {
        if (state.invoicesRequestId !== action.meta.requestId) return
        state.fetchingSection = false
        state.invoicesRequestId = null
        delete state.listRefreshRequestIds.invoices

        state.invoices = afterFetch(
          state.invoices,
          action?.payload.data,
          action?.payload?.isMore
        )
      })
      .addCase(fetchInvoices.rejected, (state, action) => {
        if (state.invoicesRequestId !== action.meta.requestId) return
        state.fetchingSection = false
        state.invoicesRequestId = null
        delete state.listRefreshRequestIds.invoices
        if (action?.meta?.arg?.refresh) return
        state.invoices = afterFailedFetch(
          state.invoices,
          initialState.invoices,
          action?.meta?.arg?.isMore
        )
      })

      // .addCase(createInvoice.fulfilled, (state, action) => {
      //   // because here, when changing the filter, there will be a data rerequest
      //   if (state.invoicesFilters.type || state.invoicesFilters.status) {
      //     state.invoicesFilters.type = initialState.invoicesFilters.type
      //     state.invoicesFilters.status = initialState.invoicesFilters.status
      //   } else {
      //     state.invoices = {
      //       ...state?.invoices,
      //       count: (state?.invoices?.count || 0) + 1,
      //       results: [action?.payload, ...(state?.invoices?.results || [])]
      //     }
      //   }
      // })

      .addCase(patchInvoice.fulfilled, (state, action) => {
        state.invoices = {
          ...state?.invoices,
          results: state?.invoices?.results?.map((m) =>
            m?.id === action?.payload?.id ? action?.payload : m
          )
        }
      })

      // deleteInvoice
      .addCase(deleteInvoice.fulfilled, (state, action) => {
        state.invoices = {
          ...state?.invoices,
          results: state?.invoices?.results?.filter(
            (m) => m?.id !== action?.payload
          )
        }
      })

      // createNewProjectSubcontractor
      .addCase(createAndAssignSubcontractor.fulfilled, (state, action) => {
        const newSubcontractor = action.payload
        const label = getEntityName(newSubcontractor, newSubcontractor?.email)

        const isExist =
          state?.checklistItemsFilterOptions?.subcontractors?.find(
            (s) => s?.value?.id === newSubcontractor?.id
          )

        // because if we try to add an existing one, then a resolved promise is returned (ok = true)
        if (!isExist) {
          state.checklistItemsFilterOptions.subcontractors = [
            {
              label,
              value: {
                id: newSubcontractor?.id,
                contact_user: newSubcontractor?.contact_user
              }
            },
            ...(state.checklistItemsFilterOptions.subcontractors || [])
          ]
        }
      })

      // fetchProjectSubcontractors
      .addCase(fetchProjectSubcontractors.pending, (state, action) => {
        if (loadMoreWaitsForRefresh(state, "subcontractors", action.meta))
          return
        state.fetchingSubcontractors = true
        state.subcontractorsRequestId = action.meta.requestId
        trackRefresh(state, "subcontractors", action.meta)

        if (!action?.meta?.arg?.isMore && !action?.meta?.arg?.refresh) {
          state.subcontractors = initialState.subcontractors
        }
      })
      .addCase(fetchProjectSubcontractors.fulfilled, (state, action) => {
        if (state.subcontractorsRequestId !== action.meta.requestId) return
        state.fetchingSubcontractors = false
        state.subcontractorsRequestId = null
        delete state.listRefreshRequestIds.subcontractors

        state.subcontractors = afterFetch(
          state.subcontractors,
          action?.payload.data,
          action?.payload?.isMore
        )
      })
      .addCase(fetchProjectSubcontractors.rejected, (state, action) => {
        if (state.subcontractorsRequestId !== action.meta.requestId) return
        state.fetchingSubcontractors = false
        state.subcontractorsRequestId = null
        delete state.listRefreshRequestIds.subcontractors
        if (action?.meta?.arg?.refresh) return
        state.subcontractors = afterFailedFetch(
          state.subcontractors,
          initialState.subcontractors,
          action?.meta?.arg?.isMore
        )
      })

      // fetchTasks
      .addCase(fetchChecklistItems.pending, (state, action) => {
        if (loadMoreWaitsForRefresh(state, "checklistItems", action.meta))
          return
        state.fetchingSection = true
        state.checklistItemsRequestId = action.meta.requestId
        trackRefresh(state, "checklistItems", action.meta)

        // A re-read after a save keeps the rows on screen, as milestones do.
        if (!action?.meta?.arg?.isMore && !action?.meta?.arg?.refresh) {
          state.checklistItems = initialState.checklistItems
        }
      })
      .addCase(fetchChecklistItems.fulfilled, (state, action) => {
        if (state.checklistItemsRequestId !== action.meta.requestId) return
        state.fetchingSection = false
        state.checklistItemsRequestId = null
        delete state.listRefreshRequestIds.checklistItems

        state.checklistItems = afterFetch(
          state.checklistItems,
          action?.payload.data,
          action?.payload?.isMore
        )
      })
      .addCase(fetchChecklistItems.rejected, (state, action) => {
        if (state.checklistItemsRequestId !== action.meta.requestId) return
        state.fetchingSection = false
        state.checklistItemsRequestId = null
        delete state.listRefreshRequestIds.checklistItems
        if (action?.meta?.arg?.refresh) return
        state.checklistItems = afterFailedFetch(
          state.checklistItems,
          initialState.checklistItems,
          action?.meta?.arg?.isMore
        )
      })

      // fetchChecklistItemsFilterOptions
      .addCase(fetchChecklistItemsFilterOptions.fulfilled, (state, action) => {
        state.checklistItemsFilterOptions = action?.payload
      })
      .addCase(fetchChecklistItemsFilterOptions.rejected, (state) => {
        state.checklistItemsFilterOptions =
          initialState.checklistItemsFilterOptions
      })

      // Replacing the row in place instead of refetching drops the fields the
      // write response omits (project_id, project_name, attachments) and leaves
      // it sitting in a filter it no longer matches.
      // // patchChecklistItem
      // .addCase(patchChecklistItem.fulfilled, (state, action) => {
      //   state.checklistItems = {
      //     ...state?.checklistItems,
      //     results: state?.checklistItems?.results?.map((m) =>
      //       m?.id === action?.payload?.id ? action?.payload : m
      //     )
      //   }
      // })

      //deleteChecklistItem
      .addCase(deleteChecklistItem.fulfilled, (state, action) => {
        state.checklistItems = {
          ...state?.checklistItems,
          results: state?.checklistItems?.results?.filter(
            (m) => m?.id !== action?.payload
          )
        }
      })

      // // createChecklistItem
      // .addCase(createChecklistItem.fulfilled, (state, action) => {
      //   state.checklistItems = {
      //     ...state?.checklistItems,
      //     count: (state?.checklistItems?.count || 0) + 1,
      //     results: [action?.payload, ...(state?.checklistItems?.results || [])]
      //   }
      // })

      // fetchProjectCommissions
      .addCase(fetchProjectCommissions.fulfilled, (state, action) => {
        state.commissions = action?.payload
      })
      .addCase(fetchProjectCommissions.rejected, (state) => {
        state.commissions = initialState.commissions
      })

      // patchProjectCommission
      .addCase(patchProjectCommission.fulfilled, (state, action) => {
        state.commissions = action?.payload
      })

      // addProjectTypeToProject
      .addCase(addProjectTypeToProject.fulfilled, (state, action) => {
        if (state?.project) {
          state.project.project_type_details = action?.payload || []
        }
      })

      // updateProjectTypeToProject
      .addCase(updateProjectTypeToProject.fulfilled, (state, action) => {
        if (state?.project) {
          state.project.project_type_details = action?.payload || []
        }
      })

      // removeProjectTypeToProject
      .addCase(removeProjectTypeToProject.fulfilled, (state, action) => {
        if (state?.project) {
          state.project.project_type_details = action?.payload || []
        }
      })

      // reorderProjectTypes
      .addCase(reorderProjectTypes.fulfilled, (state, action) => {
        if (state?.project) {
          state.project.project_type_details = action?.payload || []
        }
      })

      // fetchFinancialAlerts
      .addCase(fetchFinancialAlerts.pending, (state, action) => {
        state.financialAlertsRequestedFor = action.meta.arg
        state.financialAlertsRequestId = action.meta.requestId
      })
      .addCase(fetchFinancialAlerts.fulfilled, (state, action) => {
        if (state.financialAlertsRequestId !== action.meta.requestId) return
        state.financialAlerts = action.payload
      })
      .addCase(fetchFinancialAlerts.rejected, (state, action) => {
        if (state.financialAlertsRequestId !== action.meta.requestId) return
        state.financialAlerts = null
        state.financialAlertsRequestedFor = null
      })
      .addCase(fetchAlertContributingMilestones.fulfilled, (state, action) => {
        state.alertContributingMilestones = action.payload
      })
      // The switch reads this flag, so it moves on the click rather than a
      // round-trip later; a failure puts it back. Only the newest toggle may
      // write, or a slower earlier one lands last and wins.
      .addCase(setFinancialAlertsMuted.pending, (state, action) => {
        state.financialAlertsMuteRequestId = action.meta.requestId
        if (state.financialAlerts) {
          state.financialAlerts.muted = action.meta.arg.muted
        }
      })
      .addCase(setFinancialAlertsMuted.fulfilled, (state, action) => {
        if (state.financialAlertsMuteRequestId !== action.meta.requestId) return
        state.financialAlertsMuteRequestId = null
        if (state.financialAlerts) {
          state.financialAlerts.muted = action.payload
        }
      })
      .addCase(setFinancialAlertsMuted.rejected, (state, action) => {
        if (state.financialAlertsMuteRequestId !== action.meta.requestId) return
        state.financialAlertsMuteRequestId = null
        if (state.financialAlerts) {
          state.financialAlerts.muted = !action.meta.arg.muted
        }
      })

      // fetchFinancialSnapshot
      .addCase(fetchFinancialSnapshot.pending, (state, action) => {
        state.fetchingSnapshotMap[FETCHING_SNAPSHOT_ENUM.FINANCIAL] = true
        state.financialSnapshotRequestedFor = action.meta.arg
        state.financialSnapshotRequestId = action.meta.requestId
      })
      .addCase(fetchFinancialSnapshot.fulfilled, (state, action) => {
        if (state.financialSnapshotRequestId !== action.meta.requestId) return
        state.financialTransactionSnapshot = action.payload
        state.fetchingSnapshotMap[FETCHING_SNAPSHOT_ENUM.FINANCIAL] = false
      })
      .addCase(fetchFinancialSnapshot.rejected, (state, action) => {
        if (state.financialSnapshotRequestId !== action.meta.requestId) return
        state.financialTransactionSnapshot =
          initialState.financialTransactionSnapshot
        state.fetchingSnapshotMap[FETCHING_SNAPSHOT_ENUM.FINANCIAL] = false
        state.financialSnapshotRequestedFor = null
      })

      // fetchInvoiceSnapshot
      .addCase(fetchInvoiceSnapshot.pending, (state, action) => {
        startPageRead(state, "invoiceSnapshot", action.meta.arg, action.meta)
        state.fetchingSnapshotMap[FETCHING_SNAPSHOT_ENUM.INVOICE] = true
      })
      .addCase(fetchInvoiceSnapshot.fulfilled, (state, action) => {
        if (!settlePageRead(state, "invoiceSnapshot", action.meta, "done"))
          return
        state.invoiceSnapshot = action.payload
        state.fetchingSnapshotMap[FETCHING_SNAPSHOT_ENUM.INVOICE] = false
      })
      .addCase(fetchInvoiceSnapshot.rejected, (state, action) => {
        if (!settlePageRead(state, "invoiceSnapshot", action.meta, "failed"))
          return
        state.invoiceSnapshot = initialState.invoiceSnapshot
        state.fetchingSnapshotMap[FETCHING_SNAPSHOT_ENUM.INVOICE] = false
      })

      // fetchProjectLogHistory — the first read and the background re-reads
      // share one request id, so only the newest answer is shown.
      .addCase(fetchProjectLogHistory.pending, (state, action) => {
        state.fetchingSection = true
        state.logs = initialState.logs
        state.logsRequestId = action.meta.requestId
      })
      .addCase(fetchProjectLogHistory.fulfilled, (state, action) => {
        state.fetchingSection = false
        if (isStaleLogsRead(state, action.meta)) return
        state.logs = action.payload
      })
      .addCase(fetchProjectLogHistory.rejected, (state, action) => {
        state.fetchingSection = false
        if (isStaleLogsRead(state, action.meta)) return
        state.logs = initialState.logs
      })
      .addCase(refreshProjectLogHistory.pending, (state, action) => {
        state.logsRequestId = action.meta.requestId
      })
      .addCase(refreshProjectLogHistory.fulfilled, (state, action) => {
        if (isStaleLogsRead(state, action.meta)) return
        if (state.project?.id !== action.meta.arg) return
        state.logs = action.payload
      })
      // A failed re-read hands its turn back, so a read still out can land.
      .addCase(refreshProjectLogHistory.rejected, (state, action) => {
        if (state.logsRequestId === action.meta.requestId) {
          state.logsRequestId = null
        }
      })

      .addCase(logout.fulfilled, () => {
        console.log("project logout")
        return initialState
      })

      // The snapshot and alerts are read from finance lines and payments, which
      // these writes do not touch, but the next screen that wants them reads
      // them again rather than trust that.
      .addMatcher(
        isAnyOf(
          createInvoice.fulfilled,
          patchInvoice.fulfilled,
          deleteInvoice.fulfilled,
          generateInvoice.fulfilled,
          issueInvoice.fulfilled,
          voidInvoice.fulfilled,
          sendInvoice.fulfilled,
          patchGeneratedInvoice.fulfilled,
          deleteGeneratedInvoice.fulfilled,
          patchProjectCommission.fulfilled
        ),
        (state) => {
          state.financialSnapshotRequestedFor = null
          state.financialAlertsRequestedFor = null
        }
      )
  }
})
/** The lists as they stand before their first read for the open project. */
export const EMPTY_MILESTONES = initialState.milestones
export const EMPTY_CHECKLIST = initialState.checklistItems

export const {
  projectSetProp,
  projectPageReadsOffered,
  projectPageReadTaken,
  projectInvoiceUpdated,
  projectSetProjectTypes,
  projectSetMilestoneOrder,
  projectSetChecklistOrder,
  projectMergeChecklistItem
} = projectSlice.actions

export default projectSlice
